import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, ShoppingCart, Package, Tag, Percent, Truck,
  Users, UserCheck, Megaphone, Star, Bell, Settings, Palette,
  Store, Menu, X, LogOut, ChevronDown, ChevronRight, Gift,
  Database, Wallet, Building2, FileText, Receipt, BadgeDollarSign, FileSignature,
  Banknote, BookOpen, CalendarClock, BarChart3, Lock, ClipboardList, ClipboardCheck, Image as ImageIcon, ShieldAlert, AlertTriangle, Eye, Wine, Bot
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colores por sección — ayuda a ubicar rápido dónde está cada cosa en el
// menú (pedido del usuario: "ventas y todas sus secciones verde, catálogo
// y promos otro color..."). Cada grupo tiene su propia paleta: color de
// encabezado, color de ícono inactivo, y fondo+texto+ícono cuando el item
// está activo. Los nombres de clase van completos (no interpolados) porque
// Tailwind purga clases que no puede ver literalmente en el código.
const GROUP_COLORS = {
  'General': {
    dot: 'bg-slate-400', header: 'text-slate-400 hover:text-slate-600',
    icon: 'text-slate-400', activeBg: 'bg-slate-100', activeText: 'text-slate-700', activeIcon: 'text-slate-600',
  },
  'Ventas': {
    dot: 'bg-emerald-500', header: 'text-emerald-600 hover:text-emerald-700',
    icon: 'text-emerald-400', activeBg: 'bg-emerald-50', activeText: 'text-emerald-700', activeIcon: 'text-emerald-600',
  },
  'Catálogo & Promos': {
    dot: 'bg-sky-500', header: 'text-sky-600 hover:text-sky-700',
    icon: 'text-sky-400', activeBg: 'bg-sky-50', activeText: 'text-sky-700', activeIcon: 'text-sky-600',
  },
  'Clientes & Comercios': {
    dot: 'bg-violet-500', header: 'text-violet-600 hover:text-violet-700',
    icon: 'text-violet-400', activeBg: 'bg-violet-50', activeText: 'text-violet-700', activeIcon: 'text-violet-600',
  },
  'Compras & Gastos': {
    dot: 'bg-amber-500', header: 'text-amber-600 hover:text-amber-700',
    icon: 'text-amber-400', activeBg: 'bg-amber-50', activeText: 'text-amber-700', activeIcon: 'text-amber-600',
  },
  'Marketing & Contenido': {
    dot: 'bg-rose-500', header: 'text-rose-600 hover:text-rose-700',
    icon: 'text-rose-400', activeBg: 'bg-rose-50', activeText: 'text-rose-700', activeIcon: 'text-rose-600',
  },
  'Configuración': {
    dot: 'bg-cyan-500', header: 'text-cyan-600 hover:text-cyan-700',
    icon: 'text-cyan-400', activeBg: 'bg-cyan-50', activeText: 'text-cyan-700', activeIcon: 'text-cyan-600',
  },
  'Sistema': {
    dot: 'bg-stone-500', header: 'text-stone-500 hover:text-stone-700',
    icon: 'text-stone-400', activeBg: 'bg-stone-100', activeText: 'text-stone-700', activeIcon: 'text-stone-600',
  },
};
const DEFAULT_GROUP_COLOR = GROUP_COLORS['General'];

const NAV_GROUPS = [
  {
    label: 'General',
    items: [
      { label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
      { label: 'Módulos activos', path: '/admin/modules', icon: Settings },
      { label: 'Estadísticas', path: '/admin/erp/analytics', icon: BarChart3 },
      { label: 'Exportar / Backup', path: '/admin/erp/export', icon: Database },
      { label: 'Cierre de caja', path: '/admin/erp/caja', icon: CalendarClock },
    ]
  },
  {
    label: 'Ventas',
    items: [
      { label: 'Facturador (Mostrador)', path: '/admin/erp/ventas', icon: Receipt },
      { label: 'Pedidos online', path: '/admin/orders', icon: ShoppingCart, badge: 'orders' },
      { label: 'Armar Reparto', path: '/admin/erp/reparto', icon: Truck, badge: 'orders' },
      { label: 'Cobros', path: '/admin/erp/cobros', icon: BadgeDollarSign },
      { label: 'Pendientes de cobro', path: '/admin/erp/comprobantes?pending=1', icon: AlertTriangle },
      { label: 'Listado Comprobantes', path: '/admin/erp/comprobantes', icon: FileText },
      { label: 'Cuentas Corrientes', path: '/admin/erp/cuentas-corrientes', icon: Wallet },
      { label: 'Resumen de cuenta', path: '/admin/erp/resumen-cliente', icon: BookOpen },
      { label: 'Vendedores (Facturador)', path: '/admin/erp/facturadores', icon: Lock },
      { label: 'Conteo de Stock', path: '/admin/erp/stock-audit', icon: ClipboardCheck },
    ]
  },
  {
    label: 'Catálogo & Promos',
    items: [
      { label: 'Productos & Precios', path: '/admin/products', icon: Package },
      { label: 'Combos', path: '/admin/combos', icon: Gift },
      { label: 'Descuentos', path: '/admin/discounts', icon: Percent },
      { label: 'Promociones', path: '/admin/promotions', icon: Gift },
      { label: 'Condiciones de Venta', path: '/admin/erp/condiciones', icon: FileSignature },
    ]
  },
  {
    label: 'Clientes & Comercios',
    items: [
      { label: 'Clientes App (Web)', path: '/admin/customers', icon: Users },
      { label: 'Clientes (App + Histórico)', path: '/admin/clientes-unificados', icon: Users },
      { label: 'Solicitudes de Comercio', path: '/admin/commerce-requests', icon: Store, badge: 'notifs_comercio' },
      { label: 'Solicitudes (clientes)', path: '/admin/upgrade-requests', icon: Bell, badge: 'notifs' },
      { label: 'Datos incompletos', path: '/admin/clientes-incompletos', icon: AlertTriangle, badge: 'incomplete' },
      { label: 'Duplicados', path: '/admin/clientes-duplicados', icon: AlertTriangle },
      { label: 'Vendedores Afiliados (Referidos)', path: '/admin/sellers', icon: UserCheck },
      { label: 'Vista previa · Onboarding', path: '/admin/preview-onboarding', icon: Eye },
    ]
  },
  {
    label: 'Compras & Gastos',
    items: [
      { label: 'Cargar compra', path: '/admin/erp/compras', icon: ShoppingCart },
      { label: 'Gastos', path: '/admin/erp/analytics?tab=expenses', icon: Wallet },
      { label: 'Pagos', path: '/admin/erp/pagos', icon: Banknote },
      { label: 'Resumen proveedor', path: '/admin/erp/resumen-proveedor', icon: BookOpen },
      { label: 'Proveedores', path: '/admin/erp/proveedores', icon: Building2 },
    ]
  },
  {
    label: 'Marketing & Contenido',
    items: [
      { label: 'Banners', path: '/admin/banners', icon: Megaphone },
      { label: 'Agente WhatsApp', path: '/admin/whatsapp-agent', icon: Bot },
      { label: 'Banner WhatsApp', path: '/admin/banner-layout', icon: ImageIcon },
      { label: 'Banner Combos', path: '/admin/combo-banner-layout', icon: ImageIcon },
      { label: 'Catálogo PNG', path: '/admin/catalog-layout', icon: ImageIcon },
      { label: 'Difusión WhatsApp', path: '/admin/broadcast', icon: Megaphone },
      { label: 'Blog', path: '/admin/blog', icon: BookOpen },
      { label: 'Encuestas', path: '/admin/surveys', icon: ClipboardList },
      { label: 'Puntos y Referidos', path: '/admin/points', icon: Star },
      { label: 'Club del Vino Bacano', path: '/admin/wine-club', icon: Wine },
      { label: 'Anuncios a clientes', path: '/admin/notifications', icon: Bell },
    ]
  },
  {
    label: 'Configuración',
    items: [
      { label: 'Tienda', path: '/admin/store', icon: Store },
      { label: 'Envíos', path: '/admin/shipping', icon: Truck },
      { label: 'Apariencia', path: '/admin/theme', icon: Palette },
    ]
  },
  {
    label: 'Sistema',
    items: [
      { label: 'Migración Legacy', path: '/admin/erp/migracion', icon: Database },
      { label: 'Auditoría', path: '/admin/erp/auditoria', icon: ClipboardList },
    ]
  },
];

// Mapea cada item del nav a su clave de módulo. Si está apagado en
// /admin/modules, el item se oculta del sidebar.
const PATH_TO_MODULE = {
  '/admin/erp/ventas': 'facturador_pos',
  '/admin/erp/comprobantes': 'comprobantes',
  '/admin/erp/cobros': 'cuentas_corrientes',
  '/admin/erp/cuentas-corrientes': 'cuentas_corrientes',
  '/admin/erp/resumen-cliente': 'cuentas_corrientes',
  '/admin/erp/resumen-proveedor': 'cuentas_corrientes',
  '/admin/erp/reparto': 'armar_reparto',
  '/admin/combos': 'combos',
  // NOTA: /admin/discounts (descuentos del catálogo web) — SIEMPRE visible.
  '/admin/erp/condiciones': 'facturador_pos',
  '/admin/erp/compras': 'compras',
  '/admin/erp/analytics?tab=expenses': 'gastos',
  '/admin/erp/pagos': 'pagos',
  '/admin/erp/analytics': 'estadisticas',
  '/admin/erp/proveedores': 'proveedores',
  '/admin/clientes-unificados': 'clientes',
  '/admin/clientes-incompletos': 'clientes',
  '/admin/clientes-duplicados': 'clientes',
  // NOTA: /admin/customers (Clientes App — usuarios registrados en la web)
  // es parte del catálogo web y SIEMPRE debe estar visible aunque el ERP
  // esté suspendido. Sólo los "clientes unificados" (que mezclan legacy ERP)
  // dependen del módulo `clientes`.
  '/admin/sellers': 'vendedor',
  '/admin/broadcast': 'broadcast',
  '/admin/banner-layout': 'broadcast',
  '/admin/combo-banner-layout': 'broadcast',
  '/admin/catalog-layout': 'broadcast',
  '/admin/banners': 'broadcast',
  '/admin/points': 'puntos',
  // NOTA: /admin/products (catálogo web) NO se mapea a un módulo ERP.
  // Es parte del catálogo web y debe estar SIEMPRE visible aunque el ERP
  // esté suspendido. El módulo `stock` del ERP se refiere al kardex/legacy.
};

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, logout: authLogout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [badges, setBadges] = useState({ orders: 0, notifs: 0, notifs_comercio: 0, incomplete: 0 });
  const [moduleFlags, setModuleFlags] = useState({});

  // Guard: acepta DOS vías de auth para el panel admin.
  //  1) Sesión Google con user.role === 'admin' (nuevo, sin TOTP).
  //  2) Sesión TOTP tradicional (bebida-bacana-admin-token) — fallback.
  useEffect(() => {
    // Esperar a que termine de cargar la sesión Google antes de decidir.
    if (authLoading) return;

    // Vía 1: usuario Google con role='admin'. No hace falta el token TOTP.
    if (user?.role === 'admin') {
      localStorage.setItem('bebida-bacana-admin', 'true');
      return;
    }

    // Vía 2: fallback al token TOTP legacy.
    const token = localStorage.getItem('bebida-bacana-admin-token');
    if (!token) {
      if (localStorage.getItem('bebida-bacana-admin') === 'true') {
        localStorage.removeItem('bebida-bacana-admin');
      }
      navigate('/admin/login', { replace: true });
      return;
    }
    axios.post(`${API}/admin/session/check`, { token }).catch(() => {
      localStorage.removeItem('bebida-bacana-admin-token');
      localStorage.removeItem('bebida-bacana-admin');
      localStorage.removeItem('bebida-bacana-admin-expires');
      navigate('/admin/login', { replace: true });
    });
  }, [navigate, location.pathname, user, authLoading]);

  useEffect(() => {
    // Re-fetch módulos en cada cambio de ruta + al montar.
    // Evita "guardé pero el sidebar no se actualiza" sin tener que recargar.
    axios.get(`${API}/module-settings`, {
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
      params: { _ts: Date.now() },  // cache-buster por las dudas
    })
      .then((r) => setModuleFlags(r.data?.modules || {}))
      .catch(() => {});
  }, [location.pathname]);

  // Filtra NAV_GROUPS según los toggles guardados.
  const isModuleEnabled = (path) => {
    const key = PATH_TO_MODULE[path];
    if (!key) return true;            // no mapeado → siempre visible
    if (!(key in moduleFlags)) return true;  // settings aún no cargado
    return !!moduleFlags[key];
  };
  const visibleGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => isModuleEnabled(it.path)) }))
    .filter((g) => g.items.length > 0);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const [ordersRes, upgradeRes, sellerRes, withdrawalRes, notifRes, incompleteRes, commerceRes] = await Promise.allSettled([
          axios.get(`${API}/admin/orders`),
          axios.get(`${API}/admin/upgrade-requests`),
          axios.get(`${API}/admin/seller-requests`),
          axios.get(`${API}/admin/withdrawal-requests`),
          axios.get(`${API}/admin/system-notifications`, { params: { only_unread: true } }),
          axios.get(`${API}/admin/customers/incomplete`, { params: { missing: 'phone_or_cuit', limit: 1 } }),
          axios.get(`${API}/admin/commerce-requests`, { params: { status: 'pending' } })
        ]);
        const lastSeen = parseInt(localStorage.getItem('admin-last-orders-count') || '0', 10);
        const totalOrders = ordersRes.status === 'fulfilled' ? ordersRes.value.data.length : 0;
        const newOrders = Math.max(0, totalOrders - lastSeen);
        // Badges separados por tipo: antes se sumaban todos en un solo contador
        // compartido ("notifs") entre "Solicitudes de Comercio" y "Solicitudes
        // (clientes)", mezclando incluso alertas de factura floja sin relación
        // → mostraba un número en Comercio sin solicitudes reales que aprobar.
        let clientesCount = 0;
        if (upgradeRes.status === 'fulfilled') clientesCount += upgradeRes.value.data.filter(r => r.status === 'pending').length;
        if (sellerRes.status === 'fulfilled') clientesCount += sellerRes.value.data.filter(r => r.status === 'pending').length;
        if (withdrawalRes.status === 'fulfilled') clientesCount += withdrawalRes.value.data.filter(r => r.status === 'pending').length;
        if (notifRes.status === 'fulfilled') clientesCount += (notifRes.value.data?.unread || 0);
        const commerceCount = commerceRes.status === 'fulfilled' ? (commerceRes.value.data?.items || []).length : 0;
        // Para incomplete sólo nos importa los que tienen saldo a cobrar (priorizar)
        const incompleteCount = incompleteRes.status === 'fulfilled'
          ? (incompleteRes.value.data?.summary?.con_saldo || 0) : 0;
        setBadges({ orders: newOrders, notifs: clientesCount, notifs_comercio: commerceCount, incomplete: incompleteCount });
      } catch (e) { /* ignore */ }
    };
    fetchBadges();
  }, [location.pathname]);

  // ===========================================================
  // Atajos de teclado globales ("usar el sistema casi sin mouse")
  // ===========================================================
  // - Enter en cualquier <input> de texto/número pasa el foco al siguiente
  //   campo visible, EXCEPTO si el propio input ya maneja Enter a su manera
  //   (ej. autocompletes del Facturador/Compras que hacen e.preventDefault()
  //   para seleccionar un resultado resaltado) — ahí no pisamos nada porque
  //   chequeamos `e.defaultPrevented` (su handler corre antes, en fase
  //   bubble, porque está en el propio elemento).
  // - F1 confirma la operación activa: dispara un evento global
  //   'app:f1-confirm' que cada modal/pantalla puede escuchar para ligar su
  //   propio botón de guardar/confirmar (ej. NewProductModal en Compras).
  // - F2 abre el Facturador. F3 abre Pagos. Van por fuera de Ctrl+F/Ctrl+P
  //   porque esos ya los usa el navegador (Buscar / Imprimir).
  useEffect(() => {
    const isTextLikeInput = (el) => {
      if (!el) return false;
      if (el.tagName === 'SELECT') return true;
      if (el.tagName !== 'INPUT') return false;
      const skip = ['checkbox', 'radio', 'button', 'submit', 'file', 'range', 'color'];
      return !skip.includes(el.type);
    };
    const handler = (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('app:f1-confirm'));
        return;
      }
      if (e.key === 'F2') {
        e.preventDefault();
        navigate('/admin/erp/ventas');
        return;
      }
      if (e.key === 'F3') {
        e.preventDefault();
        navigate('/admin/erp/pagos');
        return;
      }
      if (e.key !== 'Enter' || e.defaultPrevented) return;
      if (!isTextLikeInput(e.target)) return;
      // Si el input está dentro de un <form>, dejamos que el navegador haga
      // su submit-on-Enter normal (muchas pantallas usan un único input
      // buscador dentro de un <form onSubmit> — pisarlo rompería esa acción).
      if (e.target.closest('form')) return;
      e.preventDefault();
      const focusable = Array.from(
        document.querySelectorAll('input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])')
      ).filter((el) => el.offsetParent !== null);
      const idx = focusable.indexOf(e.target);
      if (idx > -1 && idx < focusable.length - 1) {
        const next = focusable[idx + 1];
        next.focus();
        if (typeof next.select === 'function') next.select();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('bebida-bacana-admin-token');
    if (token) {
      axios.post(`${API}/admin/session/logout`, { token }).catch(() => {});
    }
    localStorage.removeItem('bebida-bacana-admin');
    localStorage.removeItem('bebida-bacana-admin-token');
    localStorage.removeItem('bebida-bacana-admin-expires');
    // Si el admin entró vía Google, cerramos también esa sesión y
    // volvemos al inicio. Si entró por TOTP tradicional, vamos al
    // login TOTP.
    if (user?.role === 'admin') {
      try { await authLogout(); } catch { /* ya cerrada */ }
      navigate('/', { replace: true });
    } else {
      navigate('/admin/login');
    }
  };

  const isActive = (path) => {
    if (path === '/admin/dashboard') return location.pathname === '/admin/dashboard';
    if (path === '/admin/products') return location.pathname === '/admin/dashboard' && false; // Products is embedded
    return location.pathname.startsWith(path);
  };

  const toggleGroup = (label) => {
    setCollapsed(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-1.5 hover:bg-zinc-100 rounded-lg" data-testid="mobile-menu-btn">
          <Menu size={22} />
        </button>
        <span className="font-bold text-sm tracking-tight">Bebida Bacana Admin</span>
        <button onClick={handleLogout} className="p-1.5 text-zinc-400 hover:text-red-500" data-testid="mobile-logout-btn">
          <LogOut size={20} />
        </button>
      </header>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-zinc-200 z-50 flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`} data-testid="admin-sidebar">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Store size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-sm tracking-tight text-zinc-900">Bebida Bacana</p>
              <p className="text-[10px] text-zinc-400 uppercase tracking-widest">Admin</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1 hover:bg-zinc-100 rounded">
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {visibleGroups.map((group) => {
            const colors = GROUP_COLORS[group.label] || DEFAULT_GROUP_COLOR;
            return (
            <div key={group.label} className="mb-1">
              <button
                onClick={() => toggleGroup(group.label)}
                className={`w-full flex items-center justify-between px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold transition-colors ${colors.header}`}
              >
                <span className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                  {group.label}
                </span>
                {collapsed[group.label] ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              </button>
              {!collapsed[group.label] && (
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const badgeCount = item.badge ? badges[item.badge] : 0;
                    return (
                      <button
                        key={item.path}
                        onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${active ? `${colors.activeBg} ${colors.activeText} font-medium` : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'}`}
                        data-testid={`nav-${item.path.split('/').pop()}`}
                      >
                        <Icon size={17} className={active ? colors.activeIcon : colors.icon} />
                        <span className="flex-1 text-left">{item.label}</span>
                        {badgeCount > 0 && (
                          <span className="min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">
                            {badgeCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-zinc-100 p-3 space-y-1">
          <button
            onClick={() => window.open('/', '_blank')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
            data-testid="nav-view-store"
          >
            <Store size={17} className="text-zinc-400" />
            Ver Tienda
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-zinc-500 hover:bg-red-50 hover:text-red-600 transition-colors"
            data-testid="admin-logout-btn"
            id="nav-logout"
          >
            <LogOut size={17} className="text-zinc-400" />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64 min-h-screen pt-14 md:pt-0 overflow-x-hidden pb-20 md:pb-8">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
